import { useMemo } from 'react';
import { get } from 'lodash';

/**
 * Build an antd Table filter config from unique values in a given column.
 *
 * @param {string|array} dataIndex - Column dataIndex (supports lodash-style path or an array like antd)
 * @param {Array<object>} rows - Table dataSource to scan for unique values
 * @param {Object} [opts]
 * @param {function(any, object): string} [opts.labelFormatter] - Optional formatter for option label; args: (rawValue, row)
 * @param {number} [opts.width] - Optional column width hint; if omitted, no width is applied
 * @param {string} [opts.nullLabel='—'] - Label to use for null/undefined values
 * @returns {{filters: Array<{text: string, value: any}>, onFilter: function, width?: number}}
 */
export const useUniqueOptionsDataGridFilter = (dataIndex, rows, opts = {}) => {
  const { labelFormatter, width, nullLabel = '—' } = opts;

  const filters = useMemo(() => {
    const seen = new Set();
    const items = [];
    if (Array.isArray(rows)) {
      for (const row of rows) {
        const raw = get(row, dataIndex);
        const key = raw == null ? '__NULL__' : String(raw);
        if (seen.has(key)) continue;
        seen.add(key);
        const text = labelFormatter
          ? labelFormatter(raw, row)
          : raw == null
            ? nullLabel
            : String(raw);
        // Preserve original value for filtering; use a sentinel for nulls
        const value = raw == null ? '__NULL__' : String(raw);
        items.push({ text, value });
      }
    }
    // Sort by label (case-insensitive, locale-aware)
    items.sort((a, b) =>
      String(a.text).localeCompare(String(b.text), undefined, {
        sensitivity: 'base',
      }),
    );
    return items;
  }, [rows, dataIndex, labelFormatter, nullLabel]);

  return {
    filters,
    onFilter: (value, record) => {
      const v = get(record, dataIndex);
      if (value === '__NULL__') return v == null;
      return String(v) === String(value);
    },
    ...(typeof width !== 'undefined' ? { width } : {}),
  };
};
